import { useContext, useEffect, useState } from "react";

import { FormType } from "@simplyk/common";
import { useSearchParams } from "next/navigation";

import { IS_PREVIEW_QUERY_PARAM } from "../../../constants/url";
import { FrontendFormContext } from "../../../contexts/FrontendFormContext";
import { useIsIframe } from "../../../hooks/useIsIframe";
import { useIsOnboarding } from "../../../hooks/useIsOnboarding";

import { getObjectFromLocalStorage } from "@/helpers/localStorage";

type StoredFormData = {
  formId: string;
  token: string;
};

interface ShouldDisplayFormOwnerBannerProps {
  currentOrganizationId: string | null | undefined;
}

export const useShouldDisplayFormOwnerBanner = ({ currentOrganizationId }: ShouldDisplayFormOwnerBannerProps) => {
  const { formType, formData } = useContext(FrontendFormContext);
  const searchParams = useSearchParams();
  const query = Object.fromEntries(searchParams.entries());

  const { isOnboarding } = useIsOnboarding();
  const isIframe = useIsIframe();
  const [storedData, setStoredData] = useState<StoredFormData>({ formId: "", token: "" });

  useEffect(() => {
    // Only access localStorage in the browser
    const storedData: StoredFormData | undefined = getObjectFromLocalStorage?.("peer-to-peer-form-data");
    if (storedData) {
      setStoredData(storedData);
    }
  }, [formData.id]);

  const isPreviewTemplateMode = query.previewTemplateMode === "true";
  const isPreview = searchParams.get(IS_PREVIEW_QUERY_PARAM) === "true" || false;

  const formOrg = formData.organization;
  const isOwner = formOrg && currentOrganizationId && formOrg?.id === currentOrganizationId;

  const paymentFormTypes = [FormType.DonationForm, FormType.Ticketing];
  const isPaymentForm = formType && paymentFormTypes.includes(formType);

  const isFormPageDonorView = formData.id;

  // When a non logged in user creates a P2P campaign, we store the formId and token in local storage
  // And we use it to display the edit and copy share link buttons
  const isPeerToPeerCreator = storedData.formId === formData.id && !!storedData.token;

  return {
    isPeerToPeerCreator,
    token: storedData.token,
    shouldDisplayFormOwnerBanner:
      (isOwner || isPeerToPeerCreator) &&
      !isOnboarding &&
      !isPreviewTemplateMode &&
      !isPreview &&
      !isIframe &&
      isPaymentForm &&
      isFormPageDonorView,
  };
};
